import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Alert,
  Typography,
} from '@mui/material';
import api from '../../utils/api';

// Mirrors CHURN_REASONS in backend/src/modules/subscription/dto/submit-churn-survey.dto.ts
const REASONS: { id: string; label: string }[] = [
  { id: 'expensive', label: 'Too expensive' },
  { id: 'features', label: 'Missing features I need' },
  { id: 'competitor', label: 'Switching to another tool' },
  { id: 'time', label: 'Not the right time' },
  { id: 'complex', label: 'Hard to set up' },
  { id: 'other', label: 'Something else' },
];

interface ChurnSurveyDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

// "Why are you letting it expire?" — shown when a clinic declines the
// end-of-trial retention offer on the paywall. Online-only surface: a failed
// submit shows the error and keeps the dialog open (fail loud).
const ChurnSurveyDialog: React.FC<ChurnSurveyDialogProps> = ({ open, onClose, onSubmitted }) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/subscription/churn-survey', {
        reason,
        note: note.trim() || undefined,
      });
      setSubmitting(false);
      onSubmitted();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Before you go — what&apos;s missing?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One honest line helps us fix it. We read every reply.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl component="fieldset" fullWidth>
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
            {REASONS.map((r) => (
              <FormControlLabel
                key={r.id}
                value={r.id}
                control={<Radio size="small" />}
                label={r.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <TextField
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="A line or two? (optional)"
          multiline
          rows={3}
          fullWidth
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 2000 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Nevermind
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!reason || submitting}
          sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6B3410' }, textTransform: 'none' }}
        >
          {submitting ? 'Sending…' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChurnSurveyDialog;
